import React, { useState, useMemo } from 'react';
import { Search } from 'lucide-react';
import './DataGrid.css';

export interface GridColumn {
  key: string;
  label: string;
  width?: string;
  sortable?: boolean;
  hideMobile?: boolean;
  hideTablet?: boolean;
  render?: (value: any, item: any) => React.ReactNode;
}

export interface GridAction {
  label?: string;
  icon: React.ReactNode;
  onClick: (item: any) => void;
  className?: string;
  title?: string;
  /** Return true to hide this action button for a specific row item */
  hidden?: (item: any) => boolean;
}

export interface DataGridProps {
  data: any[];
  columns: GridColumn[];
  actions?: GridAction[];
  loading?: boolean;
  pageSize?: number;
  onSearch?: (term: string) => void;
  searchPlaceholder?: string;
  emptyMessage?: string;
  /**
   * Rich empty state (e.g. <EmptyState …/>) shown when the grid has no data at
   * all. A search that matches nothing still shows the plain emptyMessage so
   * the user knows it is their filter, not an empty system.
   */
  emptyState?: React.ReactNode;
  rowsPerPageOptions?: number[];
}

const DataGrid: React.FC<DataGridProps> = ({
  data,
  columns,
  actions,
  loading = false,
  pageSize: initialPageSize = 10,
  onSearch,
  searchPlaceholder = 'Search...',
  emptyMessage = 'No data found',
  emptyState,
  rowsPerPageOptions = [5, 10, 25, 50],
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(initialPageSize);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

  const handleSearch = (value: string) => {
    setSearchTerm(value);
    setCurrentPage(1);
    onSearch?.(value);
  };

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortOrder('asc');
    }
    setCurrentPage(1);
  };

  const renderSortIcon = (column: string) => {
    if (sortColumn !== column) {
      return <span className="grid-sort-icon">⇅</span>;
    }
    return <span className="grid-sort-icon">{sortOrder === 'asc' ? '↑' : '↓'}</span>;
  };

  const sortedData = useMemo(() => {
    let result = [...data];

    // Filter by search term
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      result = result.filter((item) =>
        columns.some((col) => {
          const value = item[col.key];
          if (value === null || value === undefined) return false;
          return String(value).toLowerCase().includes(term);
        })
      );
    }

    // Sort
    if (sortColumn) {
      result.sort((a, b) => {
        let aVal: any = a[sortColumn];
        let bVal: any = b[sortColumn];

        if (typeof aVal === 'string') {
          aVal = aVal.toLowerCase();
          bVal = bVal.toLowerCase();
          return sortOrder === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
        }

        if (sortOrder === 'asc') {
          return aVal > bVal ? 1 : aVal < bVal ? -1 : 0;
        } else {
          return aVal < bVal ? 1 : aVal > bVal ? -1 : 0;
        }
      });
    }

    return result;
  }, [data, sortColumn, sortOrder, searchTerm, columns]);

  const totalPages = Math.ceil(sortedData.length / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = Math.min(startIndex + pageSize, sortedData.length);
  const paginatedData = sortedData.slice(startIndex, endIndex);

  return (
    <div className="data-grid">
      <div className="grid-pagination-header">
        <div className="grid-pagination-left">
          <label className="grid-rows-per-page-label">Rows per page:</label>
          <select
            className="grid-rows-select"
            value={pageSize}
            onChange={(e) => {
              setPageSize(parseInt(e.target.value));
              setCurrentPage(1);
            }}
          >
            {rowsPerPageOptions.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>

        <div className="grid-search-container">
          <Search className="grid-search-icon" size={18} strokeWidth={2} />
          <input
            type="text"
            className="grid-search-input"
            placeholder={searchPlaceholder}
            value={searchTerm}
            onChange={(e) => handleSearch(e.target.value)}
          />
        </div>

        <div className="grid-pagination-controls">
          <button
            className="grid-pagination-btn"
            onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
            disabled={currentPage === 1}
            title="Previous page"
          >
            ‹
          </button>
          <span className="grid-page-indicator">
            Page {currentPage} of {totalPages}
          </span>
          <button
            className="grid-pagination-btn"
            onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
            disabled={currentPage === totalPages}
            title="Next page"
          >
            ›
          </button>
        </div>
      </div>

      <div className="grid-card">
        {loading ? (
          <div className="grid-loading-container">
            <div className="grid-spinner-border" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : data.length === 0 && emptyState ? (
          emptyState
        ) : paginatedData.length === 0 ? (
          <div className="grid-empty-container">
            <p className="grid-empty-text">{emptyMessage}</p>
          </div>
        ) : (
          <div className="grid-table-responsive">
            <table className="grid-table">
              <thead>
                <tr>
                  {columns.map((column) => (
                    <th
                      key={column.key}
                      className={`grid-col-${column.key} ${column.sortable ? 'grid-sortable' : ''} ${column.hideMobile ? 'grid-hide-mobile' : ''} ${column.hideTablet ? 'grid-hide-tablet' : ''}`}
                      style={{ width: column.width }}
                      onClick={() => column.sortable && handleSort(column.key)}
                    >
                      {column.label}
                      {column.sortable && renderSortIcon(column.key)}
                    </th>
                  ))}
                  {actions && <th className="grid-col-actions">ACTIONS</th>}
                </tr>
              </thead>
              <tbody>
                {paginatedData.map((item, index) => (
                  <tr key={index}>
                    {columns.map((column) => (
                      <td
                        key={`${index}-${column.key}`}
                        className={`grid-col-${column.key} ${column.hideMobile ? 'grid-hide-mobile' : ''} ${column.hideTablet ? 'grid-hide-tablet' : ''}`}
                      >
                        {column.render ? column.render(item[column.key], item) : item[column.key]}
                      </td>
                    ))}
                    {actions && (
                      <td className="grid-col-actions">
                        {actions.map((action, i) => {
                          if (action.hidden?.(item)) return null;
                          return (
                            <button
                              key={i}
                              className={`grid-action-btn ${action.className || ''}`}
                              onClick={() => action.onClick(item)}
                              title={action.title}
                            >
                              {action.icon}
                            </button>
                          );
                        })}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default DataGrid;
